import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import AuthShell from '../components/auth/AuthShell'
import LanguagePicker from '../components/profile/LanguagePicker'
import MaterialIcon from '../components/ui/MaterialIcon'
import StatusPill from '../components/ui/StatusPill'
import { KILO } from '../constants/KILO_THEME'
import { useUserLanguage } from '../hooks/useUserLanguage'
import { authSignupLanguageMetadata, saveProfileLanguage, syncLanguageForUser } from '../lib/languagePreference'
import { ensureProfile } from '../lib/profile'
import { supabase } from '../lib/supabaseClient'
import { captureTrainerInviteFromUrl, peekTrainerInviteToken, processPendingTrainerInvite } from '../lib/trainerInvite'
import { markTrainerPortalSession, trainerPortalEntryUrl } from '../lib/trainerPortal'

const FEATURES = [
  { icon: 'exercise', labelKey: 'auth.featureTraining' },
  { icon: 'restaurant', labelKey: 'auth.featureMeals' },
  { icon: 'shopping_cart', labelKey: 'auth.featureShopping' },
] as const

const inputClass = `w-full ${KILO.field} rounded-2xl py-3 text-sm`

const Auth = () => {
  const { t } = useTranslation()
  const { language, changeLanguage } = useUserLanguage()
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const invitedEmail = captureTrainerInviteFromUrl()

    void (async () => {
      if (invitedEmail) {
        setEmail(invitedEmail)
        setMode('signin')
      }

      const { data: { session } } = await supabase.auth.getSession()

      if (session && peekTrainerInviteToken()) {
        const result = await processPendingTrainerInvite()
        if (result.status === 'linked' || result.status === 'already_linked') {
          navigate('/app?tab=coach')
          return
        }
        if (result.status === 'wrong_account') {
          setError(`You're signed in as a different account. Sign out, then sign in as ${result.invitedEmail}.`)
          return
        }
        if (result.status === 'expired') {
          setError('This coach invite has expired. Ask your coach for a new link.')
          return
        }
        if (result.status === 'error') {
          setError(result.message)
          return
        }
      }

      if (invitedEmail && !session) {
        setMessage('You already have Kilo or need to sign in — use the invited email to connect with your coach.')
      }
    })()
  }, [navigate])

  const afterAuthSuccess = async () => {
    const result = await processPendingTrainerInvite()
    if (result.status === 'wrong_account') {
      setError(`Sign in as ${result.invitedEmail} to accept this coach invite.`)
      await supabase.auth.signOut()
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError('')
    setMessage('')
    setLoading(true)

    try {
      if (mode === 'signin') {
        const { data, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        })

        if (signInError) {
          setError(signInError.message)
          return
        }

        if (data.session) {
          await syncLanguageForUser(data.session.user)
          const { profile, error: profileError } = await ensureProfile(data.session.user)
          if (profileError) {
            setError(profileError)
            return
          }

          await afterAuthSuccess()

          if (profile?.onboarding_completed) {
            navigate('/app?tab=coach')
          } else {
            navigate('/onboarding')
          }
        }
      } else {
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: authSignupLanguageMetadata(language),
          },
        })

        if (signUpError) {
          setError(signUpError.message)
          return
        }

        if (signUpData.user) {
          await ensureProfile(signUpData.user, { languagePreference: language })
          if (signUpData.session) {
            await saveProfileLanguage(signUpData.user.id, language)
            await afterAuthSuccess()
          }
        }

        navigate('/verify-email', { state: { email } })
        return
      }
    } catch (submitError) {
      console.error(submitError)
      setError(t('auth.unexpectedError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell
      title={mode === 'signin' ? t('auth.welcomeBack') : t('auth.createAccount')}
      subtitle={mode === 'signin' ? t('auth.signInSubtitle') : t('auth.signUpSubtitle')}
      icon="bolt"
      footer={
        <div className="space-y-4">
          <div>
            <p className={`mb-2 text-[10px] font-mono uppercase tracking-wider ${KILO.text.soft}`}>{t('auth.language')}</p>
            <LanguagePicker compact value={language} onChange={(next) => void changeLanguage(next)} />
          </div>
          <p className={`text-xs leading-relaxed ${KILO.text.soft}`}>{t('auth.privacyFooter')}</p>
          <p className={`text-xs ${KILO.text.soft}`}>
            Coach?{' '}
            <a
              href={trainerPortalEntryUrl()}
              onClick={() => markTrainerPortalSession()}
              className="font-medium text-kilo-accent-dark underline-offset-2 hover:underline"
            >
              Sign in to the trainer dashboard →
            </a>
          </p>
        </div>
      }
    >
      <div className="mb-5 flex flex-wrap justify-center gap-2">
        {FEATURES.map((feature) => (
          <span
            key={feature.labelKey}
            className="inline-flex items-center gap-1.5 rounded-full border border-kilo-border bg-kilo-nested px-3 py-1 text-[10px] font-mono uppercase tracking-wider text-kilo-muted"
          >
            <MaterialIcon name={feature.icon} size="sm" />
            {t(feature.labelKey)}
          </span>
        ))}
      </div>

      <div className="mb-6 flex justify-center gap-2">
        <StatusPill label={t('auth.signIn')} active={mode === 'signin'} onClick={() => setMode('signin')} />
        <StatusPill label={t('auth.signUp')} active={mode === 'signup'} onClick={() => setMode('signup')} />
      </div>

      <form className="space-y-4" onSubmit={handleSubmit}>
        <label className="block space-y-2">
          <span className={`${KILO.meta} ${KILO.text.soft}`}>{t('common.email')}</span>
          <div className="relative">
            <MaterialIcon name="mail" size="sm" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className={`${inputClass} pl-10`}
              placeholder={t('auth.emailPlaceholder')}
              required
              autoComplete="email"
            />
          </div>
        </label>

        <label className="block space-y-2">
          <span className={`${KILO.meta} ${KILO.text.soft}`}>{t('common.password')}</span>
          <div className="relative">
            <MaterialIcon name="lock" size="sm" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className={`${inputClass} pl-10`}
              placeholder={t('auth.passwordPlaceholder')}
              required
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            />
          </div>
        </label>

        {error ? (
          <p className="rounded-2xl border border-kilo-danger-border bg-kilo-danger-bg px-4 py-3 text-sm text-kilo-danger">{error}</p>
        ) : null}
        {message ? (
          <p className="rounded-2xl border border-kilo-accent/30 bg-kilo-accent/10 px-4 py-3 text-sm text-kilo-accent">{message}</p>
        ) : null}

        <button
          type="submit"
          disabled={loading}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-kilo-accent text-base font-bold text-black transition hover:bg-kilo-accent-dark disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-black/30 border-t-black" />
              {t('common.working')}
            </>
          ) : (
            <>
              <MaterialIcon name={mode === 'signin' ? 'login' : 'person_add'} size="sm" />
              {mode === 'signin' ? t('auth.signIn') : t('auth.signUp')}
            </>
          )}
        </button>
      </form>
    </AuthShell>
  )
}

export default Auth
